import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { getFormularios, getFormularioRespuestas, getCandidatos, getRevisiones, getRevisionTimeline, updateMe } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import { useUnreadRespuestas } from "@/contexts/UnreadRespuestasContext";
import { useUnreadRevisiones } from "@/contexts/UnreadRevisionesContext";
import { useUnopenedCandidatos } from "@/contexts/UnopenedCandidatosContext";
import { useChat } from "@/contexts/ChatContext";
import { UserAvatar, AVATAR_VARIANTS, AVATAR_PALETTES } from "@/components/UserAvatar";
import {
  Loader2, LogOut, CheckCheck, Shuffle,
  Users, FileText, Presentation, ClipboardCheck, Truck, FolderOpen, BarChart3, Mail, Star, Package, Boxes, Shield,
  Building2, MessagesSquare, KanbanSquare,
} from "lucide-react";
import { MobileNavPill } from "@/components/MobileNavPill";
import { toast } from "sonner";

const TITLE_HEADING_CLASSES = {
  h1: "text-lg font-semibold tracking-tight m-0",
  h5: "text-sm font-semibold tracking-tight m-0",
};

/**
 * Cabecera unificada (misma UX que Formularios): logo, título, navegación principal, menú usuario.
 * @param {"h1"|"h5"} [titleAs] — Nivel de encabezado del título (p. ej. h5 en Formularios/Revisiones por jerarquía respecto al contenido).
 */
export function AppMainHeader({ title, titleSuffix = "", titleAs = "h1" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser, logout } = useAuth();
  const { getDisplayName } = useSettings();
  const { markAllRespuestasAsRead, totalUnreadRespuestas } = useUnreadRespuestas();
  const { markRevisionTimelineRead, totalUnreadRevisiones } = useUnreadRevisiones();
  const { markAllCandidatosOpened, unopenedCandidatosCount } = useUnopenedCandidatos();
  const { totalNoLeidos: totalNoLeidosChat } = useChat();
  const [markingAllRead, setMarkingAllRead] = useState(false);

  const handleRandomizeAvatar = async () => {
    if (!user) return;
    const nextVariant = AVATAR_VARIANTS[Math.floor(Math.random() * AVATAR_VARIANTS.length)];
    // Nunca repetir la misma combinación que ya tiene
    let nextColor;
    do {
      nextColor = AVATAR_PALETTES[Math.floor(Math.random() * AVATAR_PALETTES.length)][0];
    } while (nextColor === user.color && nextVariant === user.avatar_variant);
    try {
      const data = await updateMe({ avatar_variant: nextVariant, color: nextColor });
      setUser((prev) => (prev ? { ...prev, avatar_variant: data.avatar_variant ?? nextVariant, color: data.color ?? nextColor } : prev));
      toast.success("Avatar actualizado");
    } catch (err) {
      const msg = err.response?.data?.detail ?? err.message ?? "Error al actualizar avatar";
      toast.error(typeof msg === "string" ? msg : "Error al actualizar avatar");
    }
  };

  const handleTodoLeido = async () => {
    setMarkingAllRead(true);
    try {
      const [formulariosData, candidatosData] = await Promise.all([
        getFormularios().catch(() => ({ formularios: [] })),
        getCandidatos({}).catch(() => ({ candidatos: [] })),
      ]);
      const formulariosList = formulariosData.formularios || [];
      const payload = await Promise.all(
        formulariosList.map((f) =>
          getFormularioRespuestas(f.id)
            .then((d) => ({ formularioId: f.id, respuestas: d.respuestas || [] }))
            .catch(() => ({ formularioId: f.id, respuestas: [] }))
        )
      );
      markAllRespuestasAsRead(payload);
      const revisionesData = await getRevisiones().catch(() => ({ revisiones: [] }));
      const revisionesList = revisionesData.revisiones || [];
      const revisionesPayload = await Promise.all(
        revisionesList.map((r) =>
          getRevisionTimeline(r.id)
            .then((d) => ({ revisionId: r.id, timelineIds: (d.timeline || []).filter((e) => e?.tipo === "respuesta_cliente").map((e) => e.id) }))
            .catch(() => ({ revisionId: r.id, timelineIds: [] }))
        )
      );
      revisionesPayload.forEach(({ revisionId, timelineIds }) => markRevisionTimelineRead(revisionId, timelineIds));
      const ids = (candidatosData.candidatos || []).map((c) => c.id).filter(Boolean);
      markAllCandidatosOpened(ids);
      toast.success("Todo marcado como leído");
    } catch (_) {
      toast.error("Error al marcar todo como leído");
    } finally {
      setMarkingAllRead(false);
    }
  };

  const path = location.pathname || "";
  const can = (perm) => user?.permissions?.[perm] !== false;
  // Un solo listado para la nav de escritorio y la píldora móvil. `matchActive: false` = sin estado activo (como hasta ahora).
  const navItems = [
    { path: "/candidatos", label: "Candidatos", icon: Users, perm: "candidatos", badge: unopenedCandidatosCount, matchActive: false },
    { path: "/chat", label: "Chat", icon: MessagesSquare, perm: "chat", badge: totalNoLeidosChat },
    { path: "/trabajos", label: "Trabajos", icon: KanbanSquare, perm: "chat", desktop: false },
    { path: "/clientes", label: "Clientes", icon: Building2, perm: "clientes" },
    { path: "/formularios", label: "Formularios", icon: FileText, perm: "formularios", badge: totalUnreadRespuestas },
    { path: "/onboarding", label: "Onboarding", icon: Presentation, perm: "onboarding" },
    { path: "/revisiones", label: "Revisiones", icon: ClipboardCheck, perm: "revisiones", badge: totalUnreadRevisiones },
    { path: "/proveedores", label: "Proveedores", icon: Truck, perm: "proveedores", matchActive: false },
    { path: "/brutalfiles", label: "Archivos", icon: FolderOpen, perm: "archivos", matchActive: false },
    { path: "/metricas", label: "Métricas", icon: BarChart3, perm: "metricas" },
    { path: "/correos", label: "Correos", icon: Mail, perm: "correos" },
    { path: "/gmb", label: "GMB", icon: Star, perm: "gmb" },
    { path: "/material", label: "Material", icon: Package, perm: "material" },
    { path: "/artefactos", label: "Artefactos", icon: Boxes, perm: "artefactos" },
    { path: "/dashboard", label: "Admin", icon: Shield, perm: "admin" },
  ]
    .filter((item) => can(item.perm))
    .map((item) => ({ ...item, active: item.matchActive !== false && path.startsWith(item.path) }));

  const HeadingTag = titleAs === "h5" ? "h5" : "h1";
  const headingClass = TITLE_HEADING_CLASSES[HeadingTag === "h5" ? "h5" : "h1"];

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-40 shrink-0 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="w-full px-6 pl-[max(1.5rem,env(safe-area-inset-left))] pr-[max(1.5rem,env(safe-area-inset-right))] h-16 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0 md:shrink-0">
          <button type="button" onClick={() => navigate("/candidatos")} className="flex items-center gap-3 shrink-0">
            <img src="/logo.png" alt="" className="h-8 w-auto" />
          </button>
          <HeadingTag className={`${headingClass} truncate`}>
            {title}
            {titleSuffix ? titleSuffix : ""}
          </HeadingTag>
        </div>
        <nav className="hidden md:flex items-center gap-1 shrink-0" aria-label="Navegación principal">
          {navItems.filter((item) => item.desktop !== false).map(({ path: itemPath, label, active, badge }) => (
            <Button
              key={itemPath}
              variant={active ? "secondary" : "ghost"}
              size="sm"
              onClick={() => navigate(itemPath)}
              className="text-sm relative"
            >
              {label}
              {badge > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[10px] font-medium px-1">
                  {badge}
                </span>
              )}
            </Button>
          ))}
        </nav>
        <div className="flex items-center gap-2 shrink-0">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex items-center gap-2 text-sm text-muted-foreground px-2 md:px-3"
                >
                  <UserAvatar user={user} size={28} />
                  <span className="hidden md:inline max-w-[120px] truncate">
                    {(getDisplayName(user) ?? user.nombre) || user.email}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56" style={{ zIndex: 9999 }}>
                <div className="flex flex-col items-center px-3 py-4 gap-3">
                  <UserAvatar user={user} size={72} className="shrink-0 ring-2 ring-muted rounded-full" />
                  <div className="text-center min-w-0 w-full">
                    <p className="text-sm font-medium truncate">{(getDisplayName(user) ?? user.nombre) || user.email}</p>
                    <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleTodoLeido} disabled={markingAllRead} onSelect={(e) => e.preventDefault()}>
                  {markingAllRead ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" strokeWidth={1.5} />
                  ) : (
                    <CheckCheck className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  )}
                  Todo leído
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleRandomizeAvatar} onSelect={(e) => e.preventDefault()}>
                  <Shuffle className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Randomizar avatar
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => {
                    logout();
                    navigate("/login");
                  }}
                  onSelect={(e) => e.preventDefault()}
                >
                  <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => navigate("/login")} className="text-sm text-muted-foreground px-2 md:px-3">
              <LogOut className="w-4 h-4 md:mr-2" strokeWidth={1.5} />
              <span className="hidden md:inline">Salir</span>
            </Button>
          )}
        </div>
        </div>
      </header>
      <div className="h-16 shrink-0" aria-hidden />
      {user && <MobileNavPill items={navItems} />}
    </>
  );
}
